// ABSREG point 4: the absence notification bell, for the top bar of the HR and COO shells.
//
// THE WORDING IS BUILT HERE, NOT ON THE SERVER (point 4c). The API returns facts only - event
// type, employee name, dates - and this component renders the sentence through i18n, so the same
// stored row reads correctly for an English and a French reader. A sentence composed server side
// would be frozen in whichever language the server picked at creation time.
//
// NEVER NOTES, NEVER MEDICAL DETAIL. There is no field to render one from: absence_notifications
// has no free-text column at all. A sick leave notification says who and which dates, exactly
// like a holiday one, which is the same limit LEAVECAL's existing sick-leave email already keeps.
//
// MEDCERT / WFHCOUNTRY: the three events the first build could not raise are now real. A
// certificate upload and a certificate going missing both fire server side (the upload path and a
// daily scheduler job), and a WFH-from-another-country request has its own sentence naming the
// country. Still no medical detail anywhere: "the certificate is missing" says a document has not
// arrived and nothing about why the person is ill.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
// WFHLIST: the shared country label, so the bell, the register and the form agree.
import { countryLabel } from './wfhCountryLabel';

import { TYPE, S, RADIUS, tokens } from './theme';
// REGOLD point 2b (2026-10-16): the gold accent is restored across the portal, and the PANEL
// below still uses it. The BELL ON THE NAVY BAR is the exception the correction keeps: a white
// icon with no box, and a RED count badge. CRIT_500 is the red the sidebar count badges already
// use, so the bell matches them rather than inventing one.
import { GOLD, CRIT_500 } from './colors';

const API = process.env.REACT_APP_API_URL || '/api';

// How often the count refreshes while the shell is open. 60s rather than a few seconds: this is a
// "somebody declared leave" signal, not a live feed, and a tighter poll would multiply requests
// across every HR and COO session for no useful gain.
const POLL_MS = 60000;

export default function NotificationBell({ dark = false, onOpenNotification }) {
  const { t } = useTranslation();
  const tk = tokens(dark);

  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/absence-register/notifications/unread-count`);
      setUnread(r.data?.unread || 0);
    } catch (e) {
      // A failed count must not break the top bar: the bell simply shows no badge. The shell is
      // shared with Sign out and the dark mode toggle, so an exception here would be far more
      // disruptive than a missing number.
    }
  }, []);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => clearInterval(id);
  }, [loadCount]);

  // Close on an outside click, so the panel behaves like every other dropdown in the shell.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/absence-register/notifications/`);
      setItems(r.data?.items || []);
      if (typeof r.data?.unread === 'number') setUnread(r.data.unread);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  // Point 4d: clicking a notification marks it read FOR THIS USER and opens the register entry.
  const clickItem = async (n) => {
    try {
      const r = await axios.post(`${API}/absence-register/notifications/${n.id}/read`);
      if (typeof r.data?.unread === 'number') setUnread(r.data.unread);
    } catch (e) {
      // Opening the entry still matters even if recording the read failed, so this does not
      // return early: the user asked to see the absence, not to update a counter.
    }
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    setOpen(false);
    if (onOpenNotification) onOpenNotification(n);
  };

  const markAll = async () => {
    try {
      const r = await axios.post(`${API}/absence-register/notifications/mark-all-read`);
      setUnread(r.data?.unread ?? 0);
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    } catch (e) {
      /* leave the panel as it is; the count refreshes on the next poll */
    }
  };

  // Point 4c's exact wording, one i18n key per event type. The date range collapses to a single
  // date when start and end are the same day, because "declared WFH on <date>" is the wording the
  // brief gives for WFH and a one-day holiday reads badly as "from X to X".
  const describe = (n) => {
    const name = n.employee_name || t('bell.someone');
    const from = n.start_date || '';
    const to = n.end_date || '';
    const oneDay = !to || to === from;
    switch (n.event_type) {
      case 'holiday_requested':
        return oneDay
          ? t('bell.holidayOn', { name, date: from })
          : t('bell.holidayRange', { name, from, to });
      case 'sick_declared':
        return oneDay
          ? t('bell.sickOn', { name, date: from })
          : t('bell.sickRange', { name, from, to });
      case 'wfh_declared':
        return oneDay
          ? t('bell.wfhOn', { name, date: from })
          : t('bell.wfhRange', { name, from, to });
      case 'wfh_country_requested': {
        // The country comes from the notification row. A request whose country somehow did not
        // survive falls back to the plain WFH sentence rather than rendering "from undefined".
        // WFHLIST point 3: the DISPLAYED label, not the stored value. A French reader saw
        // "Royaume-Uni" nowhere before this: the bell printed the stored English string.
        // countryLabel falls back to the stored value when there is no translation, so a
        // legacy country still renders rather than going blank.
        const country = countryLabel(t, n.wfh_country);
        if (!country) {
          return oneDay ? t('bell.wfhOn', { name, date: from })
                        : t('bell.wfhRange', { name, from, to });
        }
        return oneDay
          ? t('bell.wfhCountryOn', { name, country, date: from })
          : t('bell.wfhCountryRange', { name, country, from, to });
      }
      case 'certificate_uploaded':
        return t('bell.certificateUploaded', { name });
      case 'certificate_missing':
        return t('bell.certificateMissing', { name });
      default:
        // An event type this build does not know about still renders as something readable
        // rather than blank, which is what makes adding the certificate events later safe.
        return t('bell.genericEvent', { name });
    }
  };

  const s = {
    box: { position: 'relative', display: 'inline-flex' },
    btn: {
      position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '32px', height: '32px', padding: 0, borderRadius: RADIUS.sm,
      // POINT 2b: NO BOX AND A WHITE GLYPH. The original 1px border with tk.tx2 text read on the
      // navy bar as a faded grey square with a dim icon inside, which is what the brief
      // describes. White on #1a2744 is about 12.6:1, so the glyph carries the contrast alone and
      // the focus ring handles keyboard visibility.
      background: 'transparent', border: 'none', cursor: 'pointer', color: '#ffffff',
      outlineOffset: '2px',
    },
    badge: {
      position: 'absolute', top: '-6px', right: '-6px', minWidth: '16px', height: '16px',
      // POINT 2b: a RED count badge, hidden at zero by the `unread > 0 &&` guard at the render
      // site. Gold on the navy bar was hard to read as a count.
      padding: '0 4px', borderRadius: RADIUS.pill, backgroundColor: CRIT_500, color: '#fff',
      ...TYPE.small, fontSize: '10px', fontWeight: 700, lineHeight: '16px', textAlign: 'center',
    },
    panel: {
      position: 'absolute', top: '38px', right: 0, zIndex: 50,
      width: '340px', maxHeight: '420px', overflowY: 'auto',
      backgroundColor: tk.sf, border: `1px solid ${tk.bd}`, borderRadius: RADIUS.md,
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    },
    head: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: S[2], padding: S[3], borderBottom: `1px solid ${tk.bd}`,
    },
    title: { ...TYPE.label, color: tk.tx3 },
    markAll: {
      ...TYPE.small, background: 'none', border: 'none', color: GOLD,
      cursor: 'pointer', padding: 0, fontWeight: 600,
    },
    item: (isRead) => ({
      display: 'block', width: '100%', textAlign: 'left',
      padding: S[3], border: 'none', borderBottom: `1px solid ${tk.bd}`,
      backgroundColor: isRead ? 'transparent' : (dark ? 'rgba(201,168,76,0.08)' : '#fffdf5'),
      cursor: 'pointer', color: tk.tx, ...TYPE.body,
    }),
    when: { ...TYPE.small, color: tk.tx3, display: 'block', marginTop: S[1] },
    empty: { ...TYPE.body, color: tk.tx3, padding: S[4], textAlign: 'center' },
  };

  return (
    <div style={s.box} ref={boxRef}>
      <button
        type="button"
        style={s.btn}
        onClick={toggle}
        aria-label={t('bell.ariaLabel', { count: unread })}
        title={t('bell.ariaLabel', { count: unread })}
      >
        {/* Inline SVG rather than a new icon dependency, matching how the shell's other icons are
            defined locally. */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span style={s.badge}>{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div style={s.panel} role="dialog" aria-label={t('bell.panelTitle')}>
          <div style={s.head}>
            <span style={s.title}>{t('bell.panelTitle')}</span>
            {/* Point 4d's mark-all action. Offered only when there is something to clear, so it
                never reads as an action with no effect. */}
            {unread > 0 && (
              <button type="button" style={s.markAll} onClick={markAll}>
                {t('bell.markAllRead')}
              </button>
            )}
          </div>
          {loading && <p style={s.empty}>{t('bell.loading')}</p>}
          {!loading && items.length === 0 && <p style={s.empty}>{t('bell.empty')}</p>}
          {!loading && items.map((n) => (
            <button key={n.id} type="button" style={s.item(n.is_read)}
                    onClick={() => clickItem(n)}>
              {describe(n)}
              <span style={s.when}>{(n.created_at || '').slice(0, 10)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
